import { bool, func, number } from "prop-types";
import { forwardRef, useCallback, useState } from "react";

import styled from "styled-components";

import { standardInputStyles } from "../DefaultInput";

const MultiLineInput = forwardRef(
  (
    {
      minHeight = 80,
      maxHeight = 300,
      staticHeight,
      onChange: onChangeArg,
      ...rest
    },
    ref
  ) => {
    const [hasReachedMaxHeight, setHasReachedMaxHeight] = useState(
      Boolean(staticHeight)
    );
    const onChange = useCallback(
      (e) => {
        if (ref.current && !staticHeight) {
          // We need to reset the height momentarily to get the correct scrollHeight for the textarea
          ref.current.style.height = "0px";
          const scrollHeight = ref.current.scrollHeight;

          // check scrollHeight value vs min & max height
          let scrollHeightWithinBounds = null;
          if (scrollHeight < minHeight) {
            scrollHeightWithinBounds = minHeight;
            setHasReachedMaxHeight(false);
          } else if (scrollHeight > maxHeight) {
            scrollHeightWithinBounds = maxHeight;
            setHasReachedMaxHeight(true);
          } else {
            scrollHeightWithinBounds = scrollHeight;
            setHasReachedMaxHeight(false);
          }

          // We then set the height directly, outside of the render loop
          // Trying to set this with state or a ref will product an incorrect value.
          ref.current.style.height = `${scrollHeightWithinBounds}px`;
        }
        onChangeArg(e);
      },
      [ref, staticHeight, onChangeArg, minHeight, maxHeight]
    );

    return (
      <>
        <TextArea
          $hasReachedMaxHeight={hasReachedMaxHeight}
          onChange={onChange}
          ref={ref}
          {...rest}
        />
        {/* This is so the textarea content doesn't overlap with the "halfway inside" label */}
        {hasReachedMaxHeight ? (
          <OverlapHidder data-cy="MultiLineInput_OverlapHider" />
        ) : null}
      </>
    );
  }
);

const TextArea = styled.textarea`
  ${standardInputStyles}
  resize: none;
  outline: none;
  overflow-y: ${({ $hasReachedMaxHeight }) =>
    $hasReachedMaxHeight ? "auto" : "hidden"};
`;

const OverlapHidder = styled.div`
  position: absolute;
  background-color: var(--fnr-input-bg, #fff);
  height: 10px;
  top: 1px;
  left: 10px;
  right: 20px;
`;

MultiLineInput.propTypes = {
  minHeight: number,
  maxHeight: number,
  onChange: func,
  staticHeight: bool,
};

export default MultiLineInput;
